import { useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { hideLoader } from "../../utils/loaderSlice";

/**
 * Custom polling hook with optional tab visibility check.
 *
 * @param refetchFn - The async function to be polled.
 * @param deps - Additional dependencies to trigger polling reset.
 * @param interval - Polling interval in milliseconds.
 * @param respectTabFocus - If true, polling pauses when tab is inactive.
 */
export function usePollingWithReset(refetchFn, deps = [], interval = 5000, respectTabFocus = true, tableName) {
  const timerRef = useRef(null);
  const latestRefetchFn = useRef(refetchFn);
  const isTabActiveRef = useRef(!respectTabFocus || document.visibilityState === "visible");
  const dispatch = useDispatch();
  // Keep latest version of refetchFn
  useEffect(() => {
    latestRefetchFn.current = refetchFn;
  }, [refetchFn]);

  // Watch tab visibility if enabled
  useEffect(() => {
    if (!respectTabFocus) return;

    const handleVisibilityChange = () => {
      isTabActiveRef.current = document.visibilityState === "visible";
      if (isTabActiveRef.current) {
        applyPollingWithReset();
      } else {
        stopPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [respectTabFocus]);

  const pollData = async () => {
    if (!respectTabFocus || isTabActiveRef.current) {
      await latestRefetchFn.current();
      dispatch(hideLoader());
      console.log("🔁 Polling triggered", tableName);
    } else {
      console.log("⏸️ Poll skipped - tab inactive");
    }
  };

  const applyPollingWithReset = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      console.log("🧹 Cleared old interval");
    }

    if (!respectTabFocus || isTabActiveRef.current) {
      // pollData(); // optional immediate fetch
      timerRef.current = setInterval(pollData, interval);
      console.log("✅ New polling set @", interval, "ms", tableName);
    } else {
      console.log("⏸️ Polling not started - tab not focused", tableName);
    }
  }, [pollData, interval, respectTabFocus, tableName, ...deps]);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      console.log("🛑 Polling manually stopped", tableName);
    }
  }, [refetchFn, tableName]);

  useEffect(() => {
    applyPollingWithReset();
    return () => {
      stopPolling();
    };
  }, [...deps]);

  return { applyPollingWithReset, stopPolling };
}
